import { useEffect, useRef, type RefObject } from 'react';
import { usePrefersReducedMotion } from '@hooks/usePrefersReducedMotion';

interface ScrollIntoViewOnRevealOptions {
  /**
   * Scroll animation duration (ms). Match the reveal/expand transition so the scroll and
   * the reveal start and finish together. Ignored under prefers-reduced-motion.
   */
  durationMs?: number;
  /** Gap (px) left above the element's top when it is top-aligned (a card taller than the viewport).
   *  A fitting card with no `getOffsetTop` centers purely and ignores this (fb5 C4). Static fallback
   *  for `getOffsetTop`. */
  offsetTop?: number;
  /**
   * Dynamic version of `offsetTop`, measured at fire time (so it can track a sticky header/tab
   * strip whose height changes). When provided it overrides `offsetTop`, and the element's top is
   * kept at least this far below the viewport top in BOTH the top-aligned and the centered case -
   * so a card never centers its header up under sticky page chrome (fb5 item 6). Falls back to
   * `offsetTop` when omitted.
   */
  getOffsetTop?: () => number;
  /**
   * Returns the element's final (post-reveal) height. Because the revealed content is
   * already mounted, this can be measured up front — letting us pick the landing position
   * before the reveal finishes and scroll concurrently with it. When the final height fits
   * the viewport the element is centered; otherwise its top is pinned near the top of the
   * viewport. Falls back to the element's current height when omitted.
   */
  getFinalHeight?: () => number;
}

// easeOutQuart — a decelerating curve close to the CSS cubic-bezier(0.25, 1, 0.5, 1) used
// for the reveal, so the scroll and the expand share the same feel (no ease-in lag).
const easeOutQuart = (t: number): number => 1 - Math.pow(1 - t, 4);

/**
 * Keeps the window scroll in step with a reveal/expand animation, in both directions:
 *
 * - When `active` flips to `true`, smoothly scroll `ref` into view *concurrently* with the
 *   reveal (centered when its final height — via `getFinalHeight` — fits the viewport,
 *   otherwise top-aligned near the top, never over-scrolling past it).
 * - When `active` flips back to `false`, reverse the motion: scroll back to wherever the
 *   user was before expanding, concurrently with the collapse.
 *
 * The element's top stays put while it grows/shrinks, so each landing target is stable for
 * the whole animation. No-op when already in place; instant jump under prefers-reduced-motion.
 *
 * Assumes the window/document is the scroll container (uses `window.scrollY`/`scrollTo`).
 * That holds for the screens this is used on (e.g. the authenticated QuestionDetails uses a
 * natural `min-h-screen` document scroll); it would be a no-op inside a nested overflow
 * scroller, so don't reuse it on screens with one without adapting the scroller.
 */
export function useScrollIntoViewOnReveal(
  ref: RefObject<HTMLElement | null>,
  active: boolean,
  {
    durationMs = 300,
    offsetTop = 16,
    getFinalHeight,
    getOffsetTop,
  }: ScrollIntoViewOnRevealOptions = {},
): void {
  const prefersReducedMotion = usePrefersReducedMotion();
  // Scroll position captured just before expanding, so collapsing can return there.
  const preRevealScrollYRef = useRef<number | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    let targetY: number;
    if (active) {
      preRevealScrollYRef.current = window.scrollY;
      const viewportHeight = window.innerHeight;
      const finalHeight = getFinalHeight ? getFinalHeight() : el.getBoundingClientRect().height;
      const fitsInViewport = finalHeight <= viewportHeight;
      // `getOffsetTop` (measured now) sources a dynamic sticky offset - e.g. the event page's
      // sticky tab strip - so the header lands BELOW it; static `offsetTop` is the fallback used
      // when top-aligning a too-tall card.
      const offset = getOffsetTop ? getOffsetTop() : offsetTop;
      const centered = (viewportHeight - finalHeight) / 2;
      // A fitting card centers. The min-gap clamp belongs ONLY to the sticky-chrome consumer
      // (getOffsetTop): it stops the header centering up under the sticky strip. A consumer with no
      // sticky chrome must NOT inherit it - clamping to offsetTop would strand a tall-but-fitting
      // card near the top instead of centering it, so center purely (fb5 C4). A card taller than the
      // viewport top-aligns at `offset`.
      const topGap = fitsInViewport
        ? getOffsetTop
          ? Math.max(centered, offset)
          : centered
        : offset;
      targetY = Math.max(0, window.scrollY + el.getBoundingClientRect().top - topGap);
    } else {
      // Collapsing: rewind to the pre-expand position (only if we recorded one).
      const back = preRevealScrollYRef.current;
      preRevealScrollYRef.current = null;
      if (back == null) return;
      targetY = back;
    }

    const startY = window.scrollY;
    const distance = targetY - startY;
    if (Math.abs(distance) < 1) return;

    if (prefersReducedMotion) {
      window.scrollTo(0, targetY);
      return;
    }

    let frame = 0;
    const start = performance.now();
    const step = (now: number) => {
      const progress = Math.min(1, (now - start) / durationMs);
      window.scrollTo(0, startY + distance * easeOutQuart(progress));
      if (progress < 1) frame = window.requestAnimationFrame(step);
    };
    frame = window.requestAnimationFrame(step);

    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [active, ref, durationMs, offsetTop, getFinalHeight, getOffsetTop, prefersReducedMotion]);
}
